/**
 * Lançamento do laudo que o laboratório devolveu.
 *
 * O formulário existe separado do registro da coleta porque os dois momentos
 * são separados no tempo — e no servidor têm permissões diferentes.
 *
 * A prévia do eSocial é carregada junto: mostra o que ainda falta ANTES de
 * transmitir, em vez de deixar o usuário descobrir pelo retorno do governo, que
 * rejeita sem dizer qual campo estava faltando.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  type SelectChangeEvent,
} from '@mui/material';
import toxicologicalExamService from '../../../services/toxicologicalExamService';
import type { ToxicologicalExam } from '../../../types/toxicologicalExam';
import { RESULTADO_LABELS } from '../../../types/toxicologicalExam';

interface Props {
  exame: ToxicologicalExam | null;
  aberto: boolean;
  onFechar: () => void;
  onSalvo: () => void;
}

export const LancarLaudoDialog: React.FC<Props> = ({ exame, aberto, onFechar, onSalvo }) => {
  const [laboratoryCnpj, setLaboratoryCnpj] = useState('');
  const [examSequenceCode, setExamSequenceCode] = useState('');
  const [result, setResult] = useState('');
  const [resultDate, setResultDate] = useState('');
  const [notes, setNotes] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [lacunas, setLacunas] = useState<string[]>([]);

  useEffect(() => {
    if (!exame) return;
    setLaboratoryCnpj(exame.laboratoryCnpj ?? '');
    setExamSequenceCode(exame.examSequenceCode ?? '');
    setResult(exame.result ?? '');
    setResultDate(exame.resultDate ?? '');
    setNotes(exame.notes ?? '');
    setErro(null);

    // Carrega o que o servidor diria ao eSocial hoje — inclui lacunas que a
    // tela nao conhece, como CRM do medico ausente no cadastro.
    void toxicologicalExamService
      .previaEsocial(exame.id)
      .then((p) => setLacunas(p.gaps))
      .catch(() => setLacunas([]));
  }, [exame]);

  const salvar = useCallback(async () => {
    if (!exame) return;
    setSalvando(true);
    setErro(null);
    try {
      await toxicologicalExamService.lancarLaudo(exame.id, {
        laboratoryCnpj: laboratoryCnpj || null,
        examSequenceCode: examSequenceCode || null,
        result: result || null,
        resultDate: resultDate || null,
        notes: notes || null,
      });
      onSalvo();
      onFechar();
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível salvar o laudo');
    } finally {
      setSalvando(false);
    }
  }, [exame, laboratoryCnpj, examSequenceCode, result, resultDate, notes, onSalvo, onFechar]);

  return (
    <Dialog open={aberto} onClose={onFechar} maxWidth="sm" fullWidth>
      <DialogTitle>Lançar laudo do exame toxicológico</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {erro && <Alert severity="error">{erro}</Alert>}

          {lacunas.length > 0 && (
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Falta para transmitir o S-2221:
              </Typography>
              {lacunas.map((l) => (
                <Typography key={l} variant="body2" component="div">
                  • {l}
                </Typography>
              ))}
            </Alert>
          )}

          <TextField
            label="CNPJ do laboratório"
            value={laboratoryCnpj}
            onChange={(e) => setLaboratoryCnpj(e.target.value)}
            fullWidth
            helperText="Pode digitar com pontuação — o sistema limpa antes de enviar ao eSocial"
          />

          <TextField
            label="Código sequencial do exame"
            value={examSequenceCode}
            onChange={(e) => setExamSequenceCode(e.target.value)}
            fullWidth
            helperText="O número de registro que o laboratório emitiu"
          />

          <FormControl fullWidth>
            <InputLabel id="resultado-label">Resultado</InputLabel>
            <Select
              labelId="resultado-label"
              label="Resultado"
              value={result}
              onChange={(e: SelectChangeEvent<string | number>) => setResult(String(e.target.value))}
            >
              <MenuItem value="">
                <em>Ainda sem resultado</em>
              </MenuItem>
              {Object.entries(RESULTADO_LABELS).map(([valor, rotulo]) => (
                <MenuItem key={valor} value={valor}>
                  {rotulo}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Data do laudo"
            type="date"
            value={resultDate}
            onChange={(e) => setResultDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Observações"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => void salvar()} disabled={salvando}>
          {salvando ? 'Salvando…' : 'Salvar laudo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LancarLaudoDialog;
